import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';
import useApi from '@propertypal/shared/src/hooks/useApi';
import { ImportantPlaceResponse } from '@propertypal/shared/src/types/important-places';
import ImportantPlacesModal from '@propertypal/web-app/src/components/property/ImportantPlacesModal.pn';
import React, { FunctionComponent, useEffect, useState } from 'react';
import Button from '../buttons/Button';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import ImportantPlacesList from '../important-places/ImportantPlacesList.pn';
import { Box } from '../layout';
import { AddPlaceContainer, CloseToModal } from '../property/PropertyImportantPlaces.style.pn';
import { Text } from '../typography';

type ImportantPlaces = ImportantPlaceResponse[] | undefined;

interface Props {
  show: boolean;
  onClose: () => void;
  onCloseToSearch: (polyIds: number[]) => Promise<boolean>;
  polyIds?: number[];
}

const CloseToSearchModal: FunctionComponent<Props> = ({ show, onClose, onCloseToSearch, polyIds }) => {
  const [searching, setSearching] = useState<boolean>(false);
  const [searchIds, setSearchIds] = useState<number[]>([]);
  const [importantPlaces, error, loading] = useApi<ImportantPlaceResponse[]>('/important-place');

  const [data, setData] = useState<ImportantPlaces>(importantPlaces);
  const [addModal, setAddModal] = useState<boolean>(false);

  const updateData = (d: ImportantPlaces, pids?: number[]) => {
    setData(d);
    if (pids?.length)
      setSearchIds(d?.filter((p) => !!pids?.some((id) => p.polygonIds.includes(id)))?.map((p) => p.id) || []);
    else setSearchIds(d?.map((p) => p.id) || []);
  };

  const onAddToSearch = (id: number, checked: boolean) => {
    setSearchIds((prevIds) => {
      // Add the id if checked is true
      if (checked) return [...prevIds, id];
      // Remove the id if checked is false
      return prevIds.filter((existingId) => existingId !== id);
    });
  };

  useEffect(() => {
    updateData(importantPlaces, polyIds);
  }, [importantPlaces, polyIds]);

  return (
    <CloseToModal
      title="Search for properties 'close to'..."
      show={show}
      onClose={() => {
        setAddModal(false);
        onClose();
      }}
      maxWidth={768}
    >
      <div data-testid="close-to-search-modal">
        <Text as="h2">Add up to five places to live close to.</Text>
        <Box mt={8}>
          <ImportantPlacesList
            data={data}
            error={error}
            loading={loading}
            setAddModal={setAddModal}
            setData={updateData}
            onAddToSearch={onAddToSearch}
            searchIds={searchIds}
          />
        </Box>
        {!!data?.length && (
          <Box display="flex" flexDirection="column" alignItems="center" gap={16} mt={20}>
            {data.length < 5 && (
              <AddPlaceContainer onClick={() => setAddModal(true)} data-testid="add-place-trigger">
                <FontAwesomeIcon icon={faPlusCircle} size={20} />
                <span>Add another place</span>
              </AddPlaceContainer>
            )}
            <Button
              disabled={!searchIds?.length || loading || searching}
              icon={faMagnifyingGlass}
              onClick={async () => {
                setSearching(true);
                await onCloseToSearch(data?.filter((d) => searchIds.includes(d.id)).flatMap((d) => d.polygonIds) || []);
                setSearching(false);
              }}
              loading={loading || searching}
              testID="close-to-search-btn"
            >
              Search
            </Button>
          </Box>
        )}
        {addModal && data && (
          <ImportantPlacesModal
            show={addModal}
            onClose={() => setAddModal(false)}
            importantPlaces={data}
            onSubmit={(ip) => {
              updateData(ip);
              setAddModal(false);
            }}
          />
        )}
      </div>
    </CloseToModal>
  );
};

export default CloseToSearchModal;
